import { EventType, eventWithTime } from '@rrweb/types'
import { GetMediatorAccessTokenParams, MediatorFns } from '../../../api'
import { EngagementCameraRecording, VideoClipLink, VideoClipLinks } from '../../../api/models'
import { BaseEventCompat, RECORDING_TYPES, RRWEB_EVENTS, RRWebEvent, upgradeEvent, VideoStateChangeTrigger } from '@simplisafe/compass-web'
import {
  CoreRRwebData,
  ProcessedRRWebEvents,
  VideoAccessError,
  VideoEndEvent,
  VideoMirrorEndEvent,
  VideoMirrorStartEvent,
  VideoStartEvent,
  VideoStateChangeEvent
} from './rrweb/types'
import { isErtDebugMode } from '../../../utils/localStorageUtils'
import { DateTime } from 'luxon'
import { getEmptySnapshot, getVideoIdentifier } from './rrweb/rrwebUtils'
import { LiveStreamCapture, ValidatedRecording } from './ertUtils'
import { getStreamingUrlWithRetry } from './getStreamingUrlWithRetry'
import { LoggerName, namedLogger } from '../../../utils/logger'

const logger = namedLogger(LoggerName.ErtPreprocessor)

export const CLEAR_VIDEO_PLACEHOLDERS_EVENT = 'clear-video-placeholders'

const MIN_CLIP_LENGTH_SECONDS = 3
const MIN_CLIP_LENGTH_MILLIS = MIN_CLIP_LENGTH_SECONDS * 1000

// These numbers can be tuned to try and achieve more accurate synchronization between the start times of the embedded videos and the reality that the specialists see
// Be aware that ERT is subject to some inaccuracy because it depends on the client clock in the monitoring center to be accurate
const KVS_LIVE_STREAM_LATENCY_MILLIS = 1100
const FLV_LIVE_STREAM_LATENCY_MILLIS = 1000

const getProcessedRRWebEvents = (webRecordings: ValidatedRecording[]): ProcessedRRWebEvents => {
  const rawEventsIn: eventWithTime[] = []
  for (const recording of webRecordings) {
    for (const segment of recording.segments) {
      // because this is a web recording, the rrwebEvents will be there, so it's ok to ignore the undefined warning.
      if (segment.rrwebEvents) {
        rawEventsIn.push(...segment.rrwebEvents)
      } else {
        // this can only happen if there is a bug in the preprocessing
        throw new Error(`Segment ${segment.segmentId} of recording ${recording.recordingId} is missing rrweb events`)
      }
    }
  }

  const videoStartEvents = new Map<string, VideoStartEvent>()
  const videoEndEvents: VideoEndEvent[] = []
  const recordingStartedOrStoppedEvents: eventWithTime[] = []
  const videoStateChangeEventsById = new Map<string, VideoStateChangeEvent[]>()
  const videoMirrorEvents: Array<VideoMirrorStartEvent | VideoMirrorEndEvent> = []
  const videoMirrorPlaceholderNames = new Set<string>()
  const clearVideoPlaceholdersEvents = []
  const fullSnapshotEvents = []
  const rawEvents = [...rawEventsIn]
  const now = Date.now()
  let timeOfFirstEvent: number | undefined
  let timeOfLastEvent: number | undefined

  // do multiple passes through the events...

  // First Pass: collect the video-start events, and get the min and max event times, and upgrade older versions of events
  for (const event of rawEvents) {
    if (!timeOfLastEvent || event.timestamp > timeOfLastEvent) {
      timeOfLastEvent = event.timestamp
    }
    if (!timeOfFirstEvent || event.timestamp < timeOfFirstEvent) {
      timeOfFirstEvent = event.timestamp
    }

    if (event.type === EventType.Custom) {
      const currentEventPayload = event.data.payload as BaseEventCompat

      // if this is an older version of an event, this will upgrade it to the latest version
      if (currentEventPayload) {
        upgradeEvent(currentEventPayload, event.data.tag as RRWebEvent)
      }

      if (event.data.tag === RRWEB_EVENTS.VIDEO_START) {
        const videoStartEvent = currentEventPayload as VideoStartEvent
        const endTime = videoStartEvent.endTimeEpoch ?? 0
        videoStartEvent.eventTime = event.timestamp
        // if the end time is in the future, set it to the current time
        videoStartEvent.endTimeEpoch = endTime > now ? now : videoStartEvent.endTimeEpoch
        calculateAndApplyOffsets(videoStartEvent, videoStartEvent)
        videoStartEvents.set(getVideoIdentifier(videoStartEvent), videoStartEvent)
      }
      if (event.data.tag === RRWEB_EVENTS.VIDEO_MIRROR_START) {
        const videoMirrorStartEvent = currentEventPayload as VideoMirrorStartEvent
        videoMirrorStartEvent.eventTime = event.timestamp
        const mirrorStartEvent = currentEventPayload as VideoMirrorStartEvent
        videoMirrorPlaceholderNames.add(mirrorStartEvent.videoMirrorPlaceholderName)
        videoMirrorEvents.push(videoMirrorStartEvent)
      }
      if (event.data.tag === RRWEB_EVENTS.VIDEO_MIRROR_END) {
        const videoMirrorEndEvent = currentEventPayload as VideoMirrorEndEvent
        videoMirrorEndEvent.eventTime = event.timestamp
        videoMirrorEvents.push(videoMirrorEndEvent)
      }
      if (event.data.tag === RRWEB_EVENTS.RECORDING_STARTED || event.data.tag === RRWEB_EVENTS.RECORDING_STOPPED) {
        recordingStartedOrStoppedEvents.push(event)
      }
    }
  }

  // Second Pass: collect the video-end events
  for (const event of rawEvents) {
    if (event.type === EventType.Custom) {
      const videoEndEvent = event.data.payload as VideoEndEvent

      if (event.data.tag === RRWEB_EVENTS.VIDEO_END) {
        videoEndEvents.push(videoEndEvent)
        const videoStartEvent = videoStartEvents.get(getVideoIdentifier(videoEndEvent))
        if (videoStartEvent) {
          const endTime = videoEndEvent.endTimeEpoch ?? 0
          // if the end time is in the future, set it to the current time
          const newEndTime = endTime > now ? now : videoEndEvent.endTimeEpoch
          if (!videoStartEvent.endTimeEpoch || (newEndTime && newEndTime > videoStartEvent.endTimeEpoch)) {
            videoStartEvent.endTimeEpoch = newEndTime
          }
        }
      }
    }
  }

  // Third Pass:
  // Add missing video-end events for any video-start events that do not have a corresponding video-end event.
  const videoStartEventsArray = Array.from(videoStartEvents.values())
  const eventsWithoutEndTime = videoStartEventsArray.filter(videoStartEvent => !videoStartEvent.endTimeEpoch)
  if (eventsWithoutEndTime.length > 0 && rawEvents.length > 0) {
    const lastEventTime = rawEvents[rawEvents.length - 1].timestamp
    for (const videoStartEvent of eventsWithoutEndTime) {
      videoStartEvent.endTimeEpoch = lastEventTime
      const missingVideoEndEvent: eventWithTime = {
        type: EventType.Custom,
        data: {
          tag: RRWEB_EVENTS.VIDEO_END,
          payload: {
            eventName: RRWEB_EVENTS.VIDEO_END,
            version: 2,
            cameraId: videoStartEvent.cameraId,
            videoPlaceholderName: videoStartEvent.videoPlaceholderName,
            startTimeEpoch: videoStartEvent.startTimeEpoch,
            endTimeEpoch: videoStartEvent.endTimeEpoch
          } satisfies VideoEndEvent
        },
        timestamp: lastEventTime
      }
      rawEvents.push(missingVideoEndEvent)
      videoEndEvents.push(missingVideoEndEvent.data.payload as VideoEndEvent)
    }
  }

  // Forth Pass:
  // 1. collect the video end and state change events,
  // 2. find the longest known duration for the video
  // 3. inject the clear-video-placeholders events
  const events: eventWithTime[] = []
  for (const event of rawEvents) {
    let eventToUse: eventWithTime | null = event
    if (event.type === EventType.Custom) {
      const videoStateChangeEvent = event.data.payload as VideoStateChangeEvent

      if (event.data.tag === RRWEB_EVENTS.VIDEO_STATE_CHANGE) {
        const videoIdentifier = getVideoIdentifier(videoStateChangeEvent)
        let videoStateChangeEvents = videoStateChangeEventsById.get(videoIdentifier)
        if (!videoStateChangeEvents) {
          videoStateChangeEvents = []
          videoStateChangeEventsById.set(videoIdentifier, videoStateChangeEvents)
        }
        videoStateChangeEvent.eventTime = event.timestamp

        // if the change event contains a more up-to-date duration, we need to update the end time of the video start event
        const videoStartEvent = videoStartEvents.get(videoIdentifier)

        if (videoStartEvent) {
          // only include the state change events that come after the video start event
          if (
            videoStateChangeEvent.eventTime > videoStartEvent.eventTime &&
            videoStateChangeEvent.trigger !== VideoStateChangeTrigger.DurationChange
          ) {
            calculateAndApplyOffsets(videoStartEvent, videoStateChangeEvent)
            videoStateChangeEvents.push(videoStateChangeEvent)
          } else {
            // this will prevent the duration change event from being included in the events array
            eventToUse = null
          }

          // use any and all video state change events to find the longest known duration for the video
          const isLive = videoStartEvent.isLive
          const startRecordingDetails = videoStartEvent.recordingDetails
          const changeRecordingDetails = videoStateChangeEvent.recordingDetails
          if (
            !isLive &&
            startRecordingDetails &&
            changeRecordingDetails &&
            ((changeRecordingDetails.durationMillis && !startRecordingDetails.durationMillis) ||
              (changeRecordingDetails.durationMillis &&
                startRecordingDetails.durationMillis &&
                changeRecordingDetails.durationMillis > startRecordingDetails.durationMillis))
          ) {
            startRecordingDetails.durationMillis = changeRecordingDetails.durationMillis
          }
        }
      }

      if (event.data.tag === RRWEB_EVENTS.RECORDING_STOPPED) {
        // insert a blank snapshot when the recording stops so that the player shows a black screen when nothing was being captured
        events.push(getEmptySnapshot(event.timestamp))
      }
    } else if (event.type === EventType.FullSnapshot) {
      // the rrweb plugin architecture does not allow us to do processing before an event, but we need to clear the video
      // placeholders right before the full snapshot, so we insert a custom event to handle that here
      const clearVideoPlaceholders: eventWithTime = {
        type: EventType.Custom,
        data: {
          tag: CLEAR_VIDEO_PLACEHOLDERS_EVENT,
          payload: {}
        },
        timestamp: event.timestamp
      }

      fullSnapshotEvents.push(event)
      clearVideoPlaceholdersEvents.push(clearVideoPlaceholders)
      events.push(clearVideoPlaceholders)
    }

    // if we didn't clear this event in the if block above, then we want to include it in the events array
    if (eventToUse) {
      events.push(eventToUse)
    }
  }

  // Fifth Pass: make sure the video lengths are at least 3 seconds long. Mediator doesn't like to serve videos that are shorter than that.
  for (const videoStartEvent of videoStartEventsArray) {
    // Identical start/end on a live stream means it never had watchable footage (zero-length end event, or the session ended
    // right after it started and the third pass backfilled the end time). Flag it before the min-length padding below hides it.
    // Exact match on purpose: other short streams are treated as watchable and padded to the minimum clip length.
    if (videoStartEvent.isLive && videoStartEvent.startTimeEpoch === videoStartEvent.endTimeEpoch) {
      videoStartEvent.liveStreamHadNoDuration = true
    }
    if (videoStartEvent.endTimeEpoch && videoStartEvent.startTimeEpoch + MIN_CLIP_LENGTH_MILLIS > videoStartEvent.endTimeEpoch) {
      videoStartEvent.endTimeEpoch = videoStartEvent.startTimeEpoch + MIN_CLIP_LENGTH_MILLIS
    }
    if (videoStartEvent.recordingDetails && (videoStartEvent.recordingDetails.durationMillis ?? 0) < MIN_CLIP_LENGTH_MILLIS) {
      videoStartEvent.recordingDetails.durationMillis = MIN_CLIP_LENGTH_MILLIS
    }
  }

  // put the video state change events in the order they occurred during recording
  for (const videoStateChangeEvents of videoStateChangeEventsById.values()) {
    videoStateChangeEvents.sort((a, b) => a.eventTime - b.eventTime)
  }

  // copy the most up-to-date recordingDetails and duration from the video-start-event to the video-state-change-event
  for (const videoStartEvent of videoStartEventsArray) {
    const videoStateChangeEvents = videoStateChangeEventsById.get(getVideoIdentifier(videoStartEvent))
    if (videoStateChangeEvents) {
      for (const videoStateChangeEvent of videoStateChangeEvents) {
        videoStateChangeEvent.recordingDetails = videoStartEvent.recordingDetails
        videoStateChangeEvent.endTimeEpoch = videoStartEvent.endTimeEpoch
      }
    }
  }

  return {
    events,
    recordingStartedOrStoppedEvents,
    videoStartEvents: videoStartEventsArray,
    videoEndEvents,
    videoMirrorEvents,
    videoMirrorPlaceholderNames,
    videoStateChangeEventsById,
    clearVideoPlaceholdersEvents,
    fullSnapshotEvents,
    timeOfFirstEvent: timeOfFirstEvent ?? 0,
    timeOfLastEvent: timeOfLastEvent ?? 0
  }
}

/**
 * The ErtPreprocessor does its work before the ErtPlayer renders anything. It takes the recording loaded from storage and gets it ready for playback.
 * It makes several passes through the events discovering the video-start events, matching them with their corresponding video-end events, determining
 * which video clips are needed, looking up the corresponding clips in the video archive and injecting streamable urls for each video clip that
 * the user has permission to see.
 * @param mediatorFns
 * @param webRecordings
 * @param locationId
 * @param engagementId
 * @param liveStreamCaptureUrlsById
 * @param includeVideo
 */
export const processRRWebEventsForErt = async (
  mediatorFns: MediatorFns,
  webRecordings: ValidatedRecording[],
  locationId: string | undefined,
  engagementId: string | undefined,
  liveStreamCaptureUrlsById: Map<string, LiveStreamCapture>,
  includeVideo = true
): Promise<ProcessedRRWebEvents> => {
  if (!locationId || !engagementId) {
    throw new Error('Location ID and Engagement ID are required to get streaming session URLs')
  }

  const processedRRWebEvents = getProcessedRRWebEvents(webRecordings)

  await addAccessTokensAndStreamingUrls(processedRRWebEvents, mediatorFns, locationId, engagementId, liveStreamCaptureUrlsById, includeVideo)

  if (isErtDebugMode()) {
    // to ease troubleshooting, save the validation errors to the processedRRWebEvents
    if (webRecordings.some(recording => recording.error)) {
      processedRRWebEvents.validationError = webRecordings[0]?.error
      processedRRWebEvents.validationErrorDescription = webRecordings[0]?.errorDescription
    }

    // @ts-expect-error - for debugging purposes
    window.printProcessedEventSummary = () => {
      printProcessedEventSummary(processedRRWebEvents)
    }

    printProcessedEventSummary(processedRRWebEvents)
  }

  return processedRRWebEvents
}

/**
 * This provides a bunch of really useful logging to the console to diagnose and troubleshoot issues with the ErtPreprocessor and/or ErtPlayer
 * @param processedRRWebEvents
 */
const printProcessedEventSummary = (processedRRWebEvents: ProcessedRRWebEvents) => {
  const coreRRwebData: CoreRRwebData = {
    recordingStartedOrStoppedEvents: processedRRWebEvents.recordingStartedOrStoppedEvents,
    videoStartEvents: processedRRWebEvents.videoStartEvents,
    videoStateChangeEventsById: processedRRWebEvents.videoStateChangeEventsById,
    videoMirrorEvents: processedRRWebEvents.videoMirrorEvents,
    videoMirrorPlaceholderNames: processedRRWebEvents.videoMirrorPlaceholderNames,
    clearVideoPlaceholdersEvents: processedRRWebEvents.clearVideoPlaceholdersEvents,
    timeOfFirstEvent: processedRRWebEvents.timeOfFirstEvent,
    timeOfLastEvent: processedRRWebEvents.timeOfLastEvent,
    validationError: processedRRWebEvents.validationError,
    validationErrorDescription: processedRRWebEvents.validationErrorDescription
  }

  const processedEventSummary: unknown[][] = []
  processedEventSummary.push(['\nPROCESSED RRWEB EVENTS:\n', processedRRWebEvents, '\n'])
  processedEventSummary.push(['\nCORE RRWEB DATA:\n', coreRRwebData, '\n'])
  processedEventSummary.push([
    'ERT Recording\nStart Time: ',
    processedRRWebEvents.timeOfFirstEvent ? new Date(processedRRWebEvents.timeOfFirstEvent).toISOString() : undefined,
    '\nEnd Time: ',
    processedRRWebEvents.timeOfLastEvent ? new Date(processedRRWebEvents.timeOfLastEvent).toISOString() : undefined,
    '\nValidation Result: ',
    processedRRWebEvents.validationErrorDescription ?? 'No missing Segments Detected'
  ])
  for (const videoStartEvent of processedRRWebEvents.videoStartEvents) {
    processedEventSummary.push([
      `videoIdentifier: ${videoStartEvent.cameraId}_${videoStartEvent.videoPlaceholderName}_${videoStartEvent.startTimeEpoch}`,
      `\neventTime: ${new Date(videoStartEvent.eventTime).toISOString()} ${Math.abs(videoStartEvent.startTimeEpoch - videoStartEvent.eventTime) > 100 ? '\n\n!!!!! eventTime is more than 100ms different than startTimeEpoch !!!!!\n' : ''}`,
      `\nstartTimeEpoch: ${new Date(videoStartEvent.startTimeEpoch).toISOString()} (${videoStartEvent.startTimeEpoch})`,
      `\nendTimeEpoch: ${videoStartEvent.endTimeEpoch ? new Date(videoStartEvent.endTimeEpoch).toISOString() : undefined} (${videoStartEvent.endTimeEpoch})`,
      videoStartEvent.isLive
        ? '\nisLive: true'
        : `\nisLive: false\nrecordingDetails: {\n\ttimestamp: ${new Date(videoStartEvent.recordingDetails?.timestamp ?? 0).toISOString()}\n\tdurationMillis: ${videoStartEvent.recordingDetails?.durationMillis ?? 'undefined'}\n}`,
      `\nliveStreamLatencyOffset: ${videoStartEvent.liveStreamLatencyOffset}`,
      `\nmediatorStartTimeOffset: ${videoStartEvent.mediatorStartTimeOffset}`,
      `\nstartPlayingAtSeconds: ${videoStartEvent.adjustedCurrentTimeSeconds}`,
      `\nrequestedStart: ${videoStartEvent.requestedStart} (${new Date(videoStartEvent.requestedStart).getTime()})`,
      `\nrequestedEnd: ${videoStartEvent.requestedEnd} (${new Date(videoStartEvent.requestedEnd).getTime()})`,
      `\nrecordingType: ${videoStartEvent.recordingType}`
    ])
  }

  for (const entry of processedEventSummary) {
    logger.debug(...entry.map(item => ({ console: item })))
  }
}

/**
 * Once we have the processedRRWebEvents, we need to calculate the appropriate access tokens and streaming URLs for each video-start event.
 * @param processedRRWebEvents
 * @param mediatorFns
 * @param locationId
 * @param engagementId
 * @param liveStreamCaptureUrlsById
 * @param includeVideo
 */
async function addAccessTokensAndStreamingUrls(
  processedRRWebEvents: ProcessedRRWebEvents,
  mediatorFns: MediatorFns,
  locationId: string,
  engagementId: string,
  liveStreamCaptureUrlsById: Map<string, LiveStreamCapture>,
  includeVideo: boolean
) {
  if (processedRRWebEvents.events.length > 0) {
    const promises: Promise<unknown>[] = []

    if (includeVideo) {
      const now = Date.now()
      const eventsNeedingTokens = processedRRWebEvents.videoStartEvents.filter(
        startEvent => !startEvent.token || now - startEvent.token.lastRefreshed > startEvent.token.lifespanInSeconds * 1000
      )
      if (eventsNeedingTokens.length > 0) {
        const firstStartEvent = eventsNeedingTokens[0]

        // we have to await this call because the access token must be in place before we call addStreamingUrl()
        await addAccessToken(firstStartEvent, mediatorFns, { locationId, engagementId })

        // we only need to call the getMediatorAccessToken function once, and then we can use the same token for all subsequent start events
        for (let i = 1; i < eventsNeedingTokens.length; i++) {
          eventsNeedingTokens[i].token = firstStartEvent.token
          eventsNeedingTokens[i].videoAccessError = firstStartEvent.videoAccessError
        }
      }
    }

    for (const startEvent of processedRRWebEvents.videoStartEvents.values()) {
      promises.push(addStreamingUrl(mediatorFns, locationId, engagementId, startEvent, liveStreamCaptureUrlsById, includeVideo))
    }

    await Promise.all(promises)
  }
}

async function addAccessToken(startEvent: VideoStartEvent, mediatorFns: MediatorFns, params: GetMediatorAccessTokenParams) {
  if (!startEvent.token) {
    try {
      startEvent.token = { ...(await mediatorFns.getMediatorAccessToken(params)), lastRefreshed: Date.now() }
    } catch (e) {
      handleGetMediatorTokenError(startEvent, e as VideoAccessError)
    }
  }
}

// this list should be ordered in order of preference for playback
const supportedPlaybackTypes = [RECORDING_TYPES.HLS, RECORDING_TYPES.FLV]

function findAvailableVideoClipLink(
  recording: EngagementCameraRecording
): { playbackType: keyof VideoClipLinks; videoClipLink: VideoClipLink } | undefined {
  for (const supportedPlaybackType of supportedPlaybackTypes) {
    if (recording.recordingUrl) {
      for (const playbackType of Object.keys(recording.recordingUrl)) {
        if (playbackType.endsWith(supportedPlaybackType)) {
          // @ts-expect-error - this is fine, but I can't get TS to understand that
          const videoClipLink: VideoClipLink = recording.recordingUrl[playbackType]
          const recordingUrl = videoClipLink?.href
          if (recordingUrl) {
            return { playbackType: playbackType as keyof VideoClipLinks, videoClipLink }
          }
        }
      }
    }
  }
}

/**
 * Adds a streaming URL to the VideoStartEvent based on the recording type and the provided parameters, if necessary.
 * @param mediatorFns
 * @param locationId
 * @param engagementId
 * @param startEvent
 * @param liveStreamCaptureUrlsById
 * @param includeVideo
 */
async function addStreamingUrl(
  mediatorFns: MediatorFns,
  locationId: string,
  engagementId: string,
  startEvent: VideoStartEvent,
  liveStreamCaptureUrlsById: Map<string, LiveStreamCapture>,
  includeVideo: boolean
) {
  if (!includeVideo) {
    addVideoAccessError(startEvent, {
      status: 0,
      error: '',
      message: `Omitted`,
      videoOmitted: true
    } satisfies VideoAccessError)
    return
  }

  if (!Object.values(RECORDING_TYPES).includes(startEvent.recordingType)) {
    addVideoAccessError(startEvent, {
      status: 0,
      error: '',
      message: `Unrecognized recording type ${startEvent.recordingType}`,
      videoOmitted: true
    } satisfies VideoAccessError)
    return
  }

  if (
    (startEvent.isLive && startEvent.startTimeEpoch === startEvent.endTimeEpoch) ||
    (!startEvent.isLive && !startEvent.recordingDetails?.durationMillis)
  ) {
    addVideoAccessError(startEvent, {
      status: 0,
      error: '',
      message: 'Video has no duration',
      videoOmitted: true
    } satisfies VideoAccessError)
    return
  }

  if (!startEvent.isLive && !startEvent.recordingDetails) {
    addVideoAccessError(startEvent, {
      status: 0,
      error: '',
      message: 'Video is missing recording details',
      videoOmitted: true
    } satisfies VideoAccessError)
    return
  }

  if (!startEvent.recordingUrl) {
    const { start, end } = getStartAndEndTimesForRecording(startEvent)
    startEvent.requestedStart = start
    startEvent.requestedEnd = end

    try {
      const recording: EngagementCameraRecording = await mediatorFns.getRecordingUrl({
        locationId,
        engagementId,
        cameraId: startEvent.cameraId,
        start,
        end
      })

      if (recording.accessAllowed) {
        recording.startTime

        const availableVideoClipLink = findAvailableVideoClipLink(recording)
        if (availableVideoClipLink) {
          // if we don't get a startTime from the call, then we assume that the requested start time is correct
          if (!recording.startTime || recording.startTime === start) {
            startEvent.playbackType = availableVideoClipLink.playbackType
            startEvent.recordingUrl = availableVideoClipLink.videoClipLink.href
          } else {
            addVideoAccessError(startEvent, {
              status: 0,
              error: '',
              message: `No video found at that time. Requested starting time of "${start}" but got starting time of "${recording.startTime}".`,
              videoOmitted: false
            } satisfies VideoAccessError)
          }
        } else {
          addVideoAccessError(startEvent, {
            status: 0,
            error: '',
            message: `No supported video type found.  Response contained these playback types: ${Object.keys(recording?.recordingUrl ?? [])}`,
            videoOmitted: false
          } satisfies VideoAccessError)
        }
      } else {
        // comes back as a 200 from mediator but with accessAllowed=false
        addVideoAccessError(startEvent, {
          status: 200,
          error: '',
          message: recording.disallowedReason ? (disallowedReasonMessages[recording.disallowedReason] ?? accessNotAllowed) : accessNotAllowed,
          videoOmitted: false
        } satisfies VideoAccessError)
      }
    } catch (e) {
      handleGetRecordingUrlError(startEvent, e as VideoAccessError)
    }
  }

  if (startEvent.recordingUrl && startEvent.playbackType && startEvent.token) {
    const videoIdentifier = getVideoIdentifier(startEvent)
    const liveStreamCapture = liveStreamCaptureUrlsById.get(videoIdentifier)

    // if we have a live stream capture URL, use that for playback instead of going to mediator
    if (liveStreamCapture) {
      startEvent.playbackType = 'video'
      startEvent.streamingSessionURL = liveStreamCapture.blobUrl
    } else if (startEvent.playbackType?.endsWith(RECORDING_TYPES.FLV)) {
      // if the recording type is FLV, we don't need to get a streaming session URL
      startEvent.streamingSessionURL = `${startEvent.recordingUrl}&audioEncoding=AAC`
    } else {
      try {
        const response = await getStreamingUrlWithRetry(mediatorFns.getStreamingUrl, startEvent.recordingUrl, startEvent.token.accessToken)
        if (response.streamingSessionURL) {
          startEvent.streamingSessionURL = response.streamingSessionURL
        } else {
          handleGetStreamingUrlError(startEvent, {
            status: 500,
            stack: '',
            message: 'No streaming session URL found in response from getRecordingUrl'
          } satisfies MediatorError)
        }
      } catch (e) {
        handleGetStreamingUrlError(startEvent, e as MediatorError)
      }
    }
  }
}

function calculateAndApplyOffsets(startEvent: VideoStartEvent, stateChangeEvent: VideoStartEvent | VideoStateChangeEvent) {
  if (startEvent.mediatorStartTimeOffset === undefined) {
    if (startEvent.isLive) {
      if (startEvent.recordingType?.endsWith(RECORDING_TYPES.FLV)) {
        startEvent.liveStreamLatencyOffset = FLV_LIVE_STREAM_LATENCY_MILLIS
      } else {
        startEvent.liveStreamLatencyOffset = KVS_LIVE_STREAM_LATENCY_MILLIS
      }
    } else {
      startEvent.liveStreamLatencyOffset = 0
    }

    const observedStartTime = startEvent.startTimeEpoch
    const actualStartTime = observedStartTime - startEvent.liveStreamLatencyOffset
    const surplusMillis = actualStartTime % 1000
    const actualStartTimeAtPrevSecond = actualStartTime - surplusMillis
    startEvent.mediatorStartTimeOffset = actualStartTimeAtPrevSecond - observedStartTime // this is a negative number that we will add to the time we request from mediator
    startEvent.adjustmentForCurrentTimeSeconds = surplusMillis / 1000 // convert to seconds
  }

  if (startEvent.adjustmentForCurrentTimeSeconds === undefined) {
    throw new Error('adjustmentForCurrentTime should have been calculated by this point')
  }

  stateChangeEvent.adjustmentForCurrentTimeSeconds = startEvent.adjustmentForCurrentTimeSeconds
  stateChangeEvent.adjustedCurrentTimeSeconds = stateChangeEvent.currentTimeSeconds + startEvent.adjustmentForCurrentTimeSeconds
  stateChangeEvent.liveStreamLatencyOffset = startEvent.liveStreamLatencyOffset
  stateChangeEvent.adjustedCurrentTimeSecondsSanityCheck =
    (stateChangeEvent.eventTime - stateChangeEvent.startTimeEpoch) / 1000 + stateChangeEvent.adjustmentForCurrentTimeSeconds // convert to seconds
}

function roundUpToNearestThousand(num: number) {
  return Math.ceil(num / 1000) * 1000
}

function getStartAndEndTimesForRecording(startEvent: VideoStartEvent): { start: string; end: string } {
  if (!startEvent.isLive && !startEvent.recordingDetails) {
    throw new Error('Recording details are required for non-live videos')
  }
  if (!startEvent.isLive && !startEvent.recordingDetails?.durationMillis) {
    throw new Error('Recording details must have durationMillis')
  }

  let start
  if (startEvent.isLive) {
    // if this calculation is done correctly, the startTimeEpoch will always land exactly on the second with no millis
    // this is intentional because mediator does not allow for millisecond precision, so we always want to start from the nearest previous whole second
    start = new Date(startEvent.startTimeEpoch + (startEvent.mediatorStartTimeOffset ?? 0)).toISOString()
  } else {
    start = startEvent.recordingDetails!.timestamp
  }

  let end: string
  if (startEvent.isLive) {
    end = new Date(roundUpToNearestThousand(startEvent.endTimeEpoch!)).toISOString()
  } else {
    const endDate: DateTime = DateTime.fromISO(startEvent.recordingDetails!.timestamp).plus({
      millisecond: startEvent.recordingDetails?.durationMillis
    })
    end = endDate.toISO() ?? ''
  }

  return { start, end }
}

export const accessNotAllowed = 'Access denied.'
export const videoClipNotAvailable = 'Video clip not available.'
type MediatorError = { status: number; stack: string; message: string }
export const museumRecordingUrlErrorMessages: Record<number, string> = {
  401: accessNotAllowed,
  403: accessNotAllowed,
  404: videoClipNotAvailable,
  500: 'An unexpected error occurred while trying to locate this video clip.'
}
export const mediatorTokenErrorMessages: Record<number, string> = {
  401: accessNotAllowed,
  403: accessNotAllowed,
  404: 'An 404 error occurred while trying to gain access to this video.',
  500: 'An unexpected error occurred while trying to gain access to this video.'
}
export const mediatorErrorMessages: Record<number, string> = {
  401: accessNotAllowed,
  403: accessNotAllowed,
  404: 'Recording does not exist.',
  429: 'Too many requests made.',
  500: 'An unexpected error occurred while trying to retrieve this video.'
}

export const disallowedReasonMessages: Record<string, string> = {
  UserNotAllowedRecordingAccess: 'User not allowed recording access.',
  CustomerOptedOutOfQualityAssurance: 'Opted out of Quality Assurance.',
  CustomerDeletedRecording: 'Customer deleted recording.'
}

const handleGetMediatorTokenError = (startEvent: VideoStartEvent, e: VideoAccessError) => {
  logger.error('Error getting access token for streaming session URL for event', startEvent, e)
  startEvent.videoAccessError = { ...e, message: mediatorTokenErrorMessages[e.status] ?? e.message }
}

const handleGetRecordingUrlError = (startEvent: VideoStartEvent, e: VideoAccessError) => {
  logger.error('Error getting mediator URL for event', startEvent, e)
  startEvent.videoAccessError = { ...e, message: museumRecordingUrlErrorMessages[e.status] ?? e.message }
}

const addVideoAccessError = (startEvent: VideoStartEvent, e: VideoAccessError) => {
  logger.error('Error getting mediator URL for event', startEvent, e)
  startEvent.videoAccessError = e
}

const handleGetStreamingUrlError = (startEvent: VideoStartEvent, e: MediatorError) => {
  logger.error('Error getting streaming session URL for event', startEvent, e)
  startEvent.videoAccessError = {
    status: e.status,
    error: e.stack,
    message: mediatorErrorMessages[e.status] ?? e.message,
    videoOmitted: false
  }
}
